import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { chatApi } from 'api/chat';
import { Spinner } from 'components/_new/Spinner';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { Divider } from 'components/_new/Divider';
import { ProgramMembership } from 'types/Membership';
import { Button } from 'components/_new/Button';
import { useChat } from './useChat';
import { ChatConversationWindow } from './ChatConversationWindow';
import { useChatContext } from './ChatContextProvider';

type ChatConversationProps = {
	threadId: number;
	onThreadLoad?: (interlocutorProgramMembership: ProgramMembership) => void;
	onBackClick?: () => void;
};

export const ChatConversation = (props: ChatConversationProps) => {
	const { threadId, onThreadLoad, onBackClick } = props;
	const { t } = useTranslation();

	const { currentProgramMembershipId } = useChatContext();
	const { queryUniqueParam } = useChat();

	const {
		data: threadData,
		isLoading: threadLoading,
		isSuccess: threadSuccess,
		isError: threadError,
	} = useQuery(
		['chat-thread', { threadId, ...queryUniqueParam }],
		() => chatApi.getThread(currentProgramMembershipId!, threadId),
		{
			enabled: Boolean(currentProgramMembershipId),
			onSuccess: ({ interlocutorProgramMembership }) => {
				onThreadLoad?.(interlocutorProgramMembership);
			},
		}
	);

	return threadLoading ? (
		<Spinner />
	) : (
		<>
			{threadError && <div>{t('chat.canNotWriteToThisUser')}</div>}
			{threadData && threadSuccess && (
				<div className="flex-1 flex flex-column gap-2 h-full overflow-y-hidden">
					<div className="flex-0 flex flex-row gap-3 w-full px-2 pt-1">
						{onBackClick && (
							<Button
								label={t('actions.back')}
								icon="arrow-left"
								iconOnly
								variant="primary-text"
								variantSize="lg"
								circled
								className="flex md:hidden"
								style={{ width: 48 }}
								onClick={onBackClick}
							/>
						)}
						<Avatar
							name={userDisplayName(threadData.interlocutorProgramMembership.user, null)}
							src={threadData.interlocutorProgramMembership.user.avatar}
							size="lg"
						/>
						<div className="flex flex-column gap-1">
							<span className="text-lg text-primary font-bold">
								{userDisplayName(threadData.interlocutorProgramMembership.user)}
							</span>
							<span>{threadData.interlocutorProgramMembership.user.email}</span>
						</div>
						<Divider />
					</div>
					<ChatConversationWindow thread={threadData} />
				</div>
			)}
		</>
	);
};
